import React, { useState, useEffect, useRef } from 'react';
import { Box, Progress, Text, VStack, Alert, AlertIcon, AlertTitle, AlertDescription, Heading, Spinner, Button, HStack, SimpleGrid, Image, Code, FormControl, FormLabel, Input, Divider, Link } from '@chakra-ui/react';
import { useToast } from '@chakra-ui/react';
import supabase from '../utils/supabaseUtils';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { exportTemplate, loadTemplate, getAllBlocksInScene } from '../utils/cesdkUtils';
import { v4 as uuidv4 } from 'uuid';
import { FaDownload } from 'react-icons/fa';

// Implementation that can work with or without a direct CESDK instance
const AutoTemplateGenerator = ({ approvalId, onClose, cesdk }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState('Initializing...');
  const [primaryImage, setPrimaryImage] = useState(null);
  const [approvedImages, setApprovedImages] = useState([]);
  const [userData, setUserData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [generatedTemplates, setGeneratedTemplates] = useState([]);
  const [debugInfo, setDebugInfo] = useState(null);
  const [customizationStep, setCustomizationStep] = useState(false);
  const [formData, setFormData] = useState({});
  const [allPlaceholders, setAllPlaceholders] = useState([]);
  const [cesdkAvailable, setCesdkAvailable] = useState(false);
  const toast = useToast();
  
  // Store reference to CESDK instance if provided
  const cesdkRef = useRef(cesdk);

  // Check if CESDK is available
  useEffect(() => {
    if (cesdk) {
      console.log("CESDK instance is available:", cesdk);
      cesdkRef.current = cesdk;
      setCesdkAvailable(true);
    } else {
      console.warn("No CESDK instance provided to AutoTemplateGenerator");
      setCesdkAvailable(false);
    }
  }, [cesdk]);

  // Handle input change for customization form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission to generate templates with custom values
  const handleCustomizationSubmit = async (e) => {
    if (e) e.preventDefault();
    
    setLoading(true);
    setProgress('Generating templates with your customizations...');
    
    try {
      await generateTemplates();
    } catch (err) {
      console.error('Error generating templates:', err);
      setError("Failed to generate templates: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const processApproval = async () => {
      try {
        setLoading(true);
        setProgress('Initializing...');
        
        // Check if CESDK is available and log status
        if (cesdkRef.current) {
          console.log("Using CESDK instance for template generation");
        } else {
          console.warn("No CESDK instance available - templates will use fallback mode");
          
          // Try to access global CESDK if available
          if (window.CreativeEditorSDK) {
            console.log("Found global CreativeEditorSDK - attempting to use it");
            cesdkRef.current = window.CreativeEditorSDK;
            setCesdkAvailable(true);
          }
        }
        
        // Step 1: Fetch approval data
        let approvalData;
        try {
          approvalData = await fetchApprovalData(approvalId);
          if (!approvalData) {
            throw new Error("No approval data found for ID: " + approvalId);
          }
        } catch (approvalError) {
          console.error("Failed to fetch approval data:", approvalError);
          setError("Failed to fetch approval data: " + approvalError.message);
          return;
        }
        
        // Step 2: Fetch user data based on approval
        setProgress('Fetching user data...');
        let userData;
        try {
          userData = await fetchUserData(approvalData.user_id, approvalData.event_id);
          setUserData(userData);
        } catch (userError) {
          console.error("Failed to fetch user data:", userError);
          // Continue with minimal user data
          userData = {
            id: approvalData.user_id,
            name: "User " + approvalData.user_id.substring(0, 6),
            event: { id: approvalData.event_id }
          };
          setUserData(userData);
        }
        
        // Step 3: Get approved images
        setProgress('Identifying images...');
        try {
          // Get all approved images
          const approvedImageList = await fetchApprovedImages(approvalData.user_id, approvalId);
          
          if (!approvedImageList || approvedImageList.length === 0) {
            console.warn('No approved images found, cannot continue without images');
            throw new Error('No approved images found for user');
          }
          
          // Store all approved images
          setApprovedImages(approvedImageList);
          console.log(`Found ${approvedImageList.length} approved images`);
          
          // Also set the primary image for backward compatibility
          if (approvalData.primary_image_id) {
            // If we have a primary image ID in the approval, find it in the approved images
            const primary = approvedImageList.find(img => img.id === approvalData.primary_image_id);
            if (primary) {
              const primaryUrl = primary.image_url || primary.file_url;
              setPrimaryImage(primaryUrl);
              console.log("Set primary image from approval data:", primaryUrl);
            } else {
              // If the primary image isn't in approved images, use the first one
              const primaryUrl = approvedImageList[0].image_url || approvedImageList[0].file_url;
              setPrimaryImage(primaryUrl);
              console.log("Primary image not found in approved images, using first image:", primaryUrl);
            }
          } else {
            // Use the first approved image as primary if none specified
            const primaryUrl = approvedImageList[0].image_url || approvedImageList[0].file_url;
            setPrimaryImage(primaryUrl);
            console.log("No primary image specified, using first approved image:", primaryUrl);
          }
        } catch (imageError) {
          console.error('Error fetching images:', imageError);
          throw new Error('Images are required: ' + imageError.message);
        }
        
        // Step 4: Fetch templates for the event
        setProgress('Fetching templates...');
        let templates;
        try {
          templates = await fetchTemplates(approvalData.event_id);
          setTemplates(templates);
          
          // Extract all unique placeholders from templates for customization form
          const allPlaceholders = [];
          const placeholderNames = new Set();
          
          templates.forEach(template => {
            let placeholderData = template.placeholder_data;
            
            // Try to parse placeholder_data if it's a string
            if (typeof placeholderData === 'string') {
              try {
                placeholderData = JSON.parse(placeholderData);
              } catch (error) {
                console.error("Error parsing placeholder_data string:", error);
                placeholderData = [];
              }
            }
            
            if (placeholderData && Array.isArray(placeholderData)) {
              placeholderData.forEach(placeholder => {
                // Only add unique text placeholders
                if (placeholder.type === '//ly.img.ubq/text' && !placeholderNames.has(placeholder.name)) {
                  placeholderNames.add(placeholder.name);
                  allPlaceholders.push(placeholder);
                }
              });
            }
          });
          
          setAllPlaceholders(allPlaceholders);
          
          // Pre-populate form data with default values
          const initialFormData = {};
          allPlaceholders.forEach(placeholder => {
            // Add user name to text fields if they exist
            const userName = userData.name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
            
            // Check if this is a name field
            if (placeholder.name.toLowerCase().includes('name')) {
              initialFormData[placeholder.name] = userName;
            } 
            // Check if this is a jersey number field
            else if (placeholder.name.toLowerCase().includes('number') || 
                     placeholder.name.toLowerCase().includes('#')) {
              // Use jersey number from user data if available, or a default
              initialFormData[placeholder.name] = userData.jersey_number || '00';
            }
            // Any other text field - use placeholder default or empty string
            else {
              initialFormData[placeholder.name] = placeholder.defaultValue || '';
            }
          });
          
          setFormData(initialFormData);
          
          // Move to customization step if we have placeholders
          if (allPlaceholders.length > 0) {
            setCustomizationStep(true);
            setLoading(false);
          } else {
            // If no customizable placeholders, proceed directly to generation
            await generateTemplates();
          }
          
        } catch (templatesError) {
          console.error("Failed to fetch templates:", templatesError);
          setError("Failed to fetch templates: " + templatesError.message);
          return;
        }
      } catch (err) {
        console.error('Error in template setup:', err);
        setError("Failed during setup: " + err.message);
        setDebugInfo(JSON.stringify({
          error: err.message,
          stack: err.stack,
          name: err.name
        }, null, 2));
        toast({
          title: "Setup failed",
          description: err.message || 'Failed to setup template generation',
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setLoading(false);
      }
    };
    
    if (approvalId) {
      processApproval();
    }
  }, [approvalId, toast]);

  // Core template generation function
  const generateTemplates = async () => {
    try {
      setLoading(true);
      setProgress('Processing templates...');
      
      // Ensure we have images to work with
      if (!approvedImages || approvedImages.length === 0) {
        throw new Error('No approved images found for template generation');
      }
      
      // Track total templates to generate
      const totalToGenerate = templates.length * approvedImages.length;
      let processedCount = 0;
      
      // Use all approved images to generate templates
      const results = [];
      
      // For each approved image
      for (let imgIndex = 0; imgIndex < approvedImages.length; imgIndex++) {
        const currentImage = approvedImages[imgIndex];
        const imageUrl = currentImage.image_url || currentImage.file_url;
        
        setProgress(`Processing image ${imgIndex + 1} of ${approvedImages.length}...`);
        console.log(`Processing image ${imgIndex + 1}/${approvedImages.length}: ${imageUrl}`);
        
        // Process each template for this image
        for (let i = 0; i < templates.length; i++) {
          const template = templates[i];
          processedCount++;
          setProgress(`Processing template ${i + 1} of ${templates.length} for image ${imgIndex + 1}/${approvedImages.length} (${Math.round((processedCount/totalToGenerate)*100)}%)...`);
          
          try {
            console.log(`Template ${template.name} placeholders:`, template.placeholder_data);
            
            // Create replacement data based on template placeholders and form data
            const replacementData = { ...formData };
            
            // Process placeholder data if available
            let placeholderData = template.placeholder_data;
            
            // Try to parse placeholder_data if it's a string
            if (typeof placeholderData === 'string') {
              try {
                placeholderData = JSON.parse(placeholderData);
              } catch (error) {
                console.error("Error parsing placeholder_data string:", error);
                placeholderData = [];
              }
            }
            
            if (placeholderData && Array.isArray(placeholderData)) {
              placeholderData.forEach(placeholder => {
                console.log("Processing placeholder:", placeholder.name, placeholder.type);
                
                // Handle image placeholders (specifically look for "Person" or any image placeholder)
                if (placeholder.type === '//ly.img.ubq/graphic' || 
                    placeholder.name.toLowerCase() === 'person' || 
                    placeholder.name.toLowerCase() === 'source') {
                  replacementData[placeholder.name] = imageUrl; // Use the current image from the loop
                } 
                // Text placeholders are already in formData from the customization step
              });
            } else {
              // Fallback when no placeholder data is available
              console.log("No placeholder data found, using generic mappings");
              
              // Default mappings for common placeholders - use the current image
              replacementData["Person"] = imageUrl;
              replacementData["person"] = imageUrl;
              replacementData["Source"] = imageUrl;
              replacementData["source"] = imageUrl;
              replacementData["Image"] = imageUrl;
              replacementData["image"] = imageUrl;
            }
            
            console.log("Final replacement data:", replacementData);
            
            let exportUrl = null;
            let localObjectUrl = null;
            
            // CESDK Approach: If CESDK is available, use it
            if (cesdkRef.current) {
              console.log(`Exporting template ${template.name} with ID: ${template.id} using CESDK`);
              try {
                // Make sure CESDK is initialized and ready
                if (typeof cesdkRef.current.editor !== 'undefined') {
                  console.log("CESDK editor is available");
                }
                
                /**
                 * Custom template export function that properly handles image proportions
                 * 
                 * KEY IMPLEMENTATION NOTES:
                 * 1. Image Scaling Fix:
                 *    - Uses createFill("image") instead of createFill('//ly.img.ubq/fill/image')
                 *    - Sets square dimensions (1080x1080) to prevent distortion
                 *    - Explicitly sets "fit" mode to maintain aspect ratio
                 *    - Centers image using position x/y = 0.5
                 * 
                 * 2. Compatibility:
                 *    - Supports both setSourceSet (newer CESDK) and setString (older CESDK)
                 *    - Uses consistent approach for both new and existing fills
                 *    - Includes proper error handling and fallbacks
                 * 
                 * 3. Best Practices:
                 *    - Sets fill on block before modifying fill properties
                 *    - Locks aspect ratio of the container
                 *    - Uses minimal delay (50ms) for fill creation to take effect
                 * 
                 * This approach matches the working implementation in NewDashboard.jsx
                 * which correctly displays primary images with proper proportions.
                 */
                const customExportTemplate = async (cesdk, templateId, replacementData) => {
                  try {
                    console.log("Starting custom export process for template:", templateId);
                    console.log("Replacement data:", replacementData);
                    
                    // Load the template
                    const loaded = await loadTemplate(cesdk, templateId);
                    console.log("Template loaded successfully:", loaded);
                    if (!loaded) throw new Error("Failed to load template");
                    
                    const scene = cesdk.engine.scene.get();
                    console.log("Scene retrieved:", !!scene);
                    if (!scene) throw new Error("No active scene loaded");
                    
                    // Get all blocks in scene
                    const blocks = getAllBlocksInScene(cesdk, scene);
                    console.log("Found blocks in scene:", blocks.length);
                    
                    // Log all blocks with their names and types
                    console.log("Blocks in scene:", blocks.map(blockId => {
                      try {
                        return {
                          id: blockId,
                          name: cesdk.engine.block.getName(blockId),
                          type: cesdk.engine.block.getType(blockId),
                          hasFill: !!cesdk.engine.block.getFill(blockId)
                        };
                      } catch (e) {
                        return { id: blockId, error: e.message };
                      }
                    }));
                    
                    // Go through each replacement data item
                    for (const [key, value] of Object.entries(replacementData)) {
                      // Find block with this name
                      console.log(`Looking for block with name "${key}"`);
                      const blockId = blocks.find(b => {
                        try {
                          return cesdk.engine.block.getName(b) === key;
                        } catch (e) {
                          console.error(`Error getting name for block ${b}:`, e);
                          return false;
                        }
                      });
                      
                      if (blockId) {
                        console.log(`Found block with ID ${blockId} for "${key}"`);
                        
                        try {
                          const type = cesdk.engine.block.getType(blockId);
                          console.log(`Block type for "${key}": ${type}`);
                          
                          // Handle text blocks
                          if (type === '//ly.img.ubq/text') {
                            cesdk.engine.block.setString(blockId, "text/text", value);
                            console.log(`Set text for ${key} to "${value}"`);
                          } 
                          // Special handling for graphic blocks (images)
                          else if (type === '//ly.img.ubq/graphic' && value.startsWith('http')) {
                            console.log(`Setting image for ${key} (${blockId}) to ${value}`);
                            
                            try {
                              // First check if the block already has a fill
                              const existingFill = cesdk.engine.block.getFill(blockId);
                              console.log(`Existing fill for "${key}":`, existingFill);
                              
                              if (!existingFill) {
                                // Create a new fill using the same approach as NewDashboard.jsx
                                console.log(`Creating new image fill for ${key}`);
                                const fillId = cesdk.engine.block.createFill("image");
                                console.log(`Created new fill with ID: ${fillId}`);
                                
                                // Ensure fill is enabled
                                cesdk.engine.block.setFillEnabled(fillId, true);
                                
                                // Set the fill on the block first
                                cesdk.engine.block.setFill(blockId, fillId);
                                
                                // Wait for the fill to be properly attached
                                await new Promise(resolve => setTimeout(resolve, 50));
                                
                                // Now set the image source using both approaches to ensure compatibility
                                try {
                                  // First try sourceSet method (newer CESDK)
                                  cesdk.engine.block.setSourceSet(fillId, "fill/image/sourceSet", [
                                    {
                                      uri: value,
                                      width: 1080,
                                      height: 1080, // Use square dimensions as a more neutral starting point
                                    },
                                  ]);
                                  console.log("Applied image with setSourceSet");
                                } catch (sourceSetError) {
                                  // Fall back to string method (older CESDK)
                                  console.log("setSourceSet failed, trying setString:", sourceSetError);
                                  cesdk.engine.block.setString(fillId, "fill/image/imageFileURI", value);
                                  console.log("Applied image with setString");
                                }
                                
                                // Apply settings that match NewDashboard.jsx approach
                                try {
                                  // Set to 'fit' mode explicitly
                                  cesdk.engine.block.setEnum(fillId, "fill/image/mode", "fit");
                                  console.log(`Set fill mode to "fit" for ${blockId}`);
                                  
                                  // Lock aspect ratio of the block
                                  cesdk.engine.block.setBool(blockId, "constraints/aspectRatio/locked", true);
                                  
                                  // Set position to center (for better centering of content)
                                  if (cesdk.engine.block.hasProperty(fillId, "fill/image/position/x")) {
                                    cesdk.engine.block.setDouble(fillId, "fill/image/position/x", 0.5);
                                    cesdk.engine.block.setDouble(fillId, "fill/image/position/y", 0.5);
                                  }
                                } catch (propError) {
                                  console.warn(`Error setting additional properties:`, propError);
                                }
                              } else {
                                // Use the existing fill with the same improved approach
                                console.log(`Using existing fill ${existingFill} for ${key}`);
                                
                                // Try both image setting methods for compatibility
                                try {
                                  // First try sourceSet (newer CESDK)
                                  cesdk.engine.block.setSourceSet(existingFill, "fill/image/sourceSet", [
                                    {
                                      uri: value,
                                      width: 1080,
                                      height: 1080, // Square dimensions for more neutral starting point
                                    },
                                  ]);
                                } catch (sourceSetError) {
                                  // Fall back to string method (older CESDK)
                                  console.log("setSourceSet failed for existing fill, trying setString:", sourceSetError);
                                  cesdk.engine.block.setString(existingFill, "fill/image/imageFileURI", value);
                                }
                                
                                // Apply the same settings for consistent behavior
                                try {
                                  // Set to 'fit' mode explicitly
                                  cesdk.engine.block.setEnum(existingFill, "fill/image/mode", "fit");
                                  
                                  // Lock aspect ratio
                                  cesdk.engine.block.setBool(blockId, "constraints/aspectRatio/locked", true);
                                  
                                  // Center the image within the fill
                                  if (cesdk.engine.block.hasProperty(existingFill, "fill/image/position/x")) {
                                    cesdk.engine.block.setDouble(existingFill, "fill/image/position/x", 0.5);
                                    cesdk.engine.block.setDouble(existingFill, "fill/image/position/y", 0.5);
                                  }
                                } catch (propError) {
                                  console.warn(`Error setting additional properties on existing fill:`, propError);
                                }
                              }
                              console.log(`Successfully set image for ${key}`);
                            } catch (fillErr) {
                              console.error(`Error setting fill for ${key}:`, fillErr);
                              
                              // Try alternative approach - create a new graphic block with the image
                              try {
                                console.log("Trying alternative approach for image replacement");
                                
                                // Try to directly export the image without template replacement
                                console.log("Attempting to export the template as-is without image replacement");
                                const exportBlob = await cesdk.engine.block.export(scene, "image/png", {
                                  targetWidth: 1920,
                                  targetHeight: 1080,
                                  dpi: 72,
                                  exportArea: "design"
                                });
                                
                                // Create a local objectURL for browser download
                                const localObjectUrl = URL.createObjectURL(exportBlob);
                                console.log("Created fallback export without image replacement:", localObjectUrl);
                                
                                // Return with limited success
                                return { 
                                  supabaseUrl: null, 
                                  localObjectUrl, 
                                  fileName: `${templateId}-export-${Date.now()}.png`,
                                  fallback: true
                                };
                              } catch (altErr) {
                                console.error("Alternative approach also failed:", altErr);
                                throw new Error("Could not replace image or export template");
                              }
                            }
                          }
                        } catch (blockErr) {
                          console.error(`Error processing block for "${key}":`, blockErr);
                        }
                      } else {
                        console.warn(`No block found with name "${key}"`);
                      }
                    }
                    
                    // Export the scene
                    try {
                      console.log("Attempting to export the scene to a Blob");
                      const exportBlob = await cesdk.engine.block.export(scene, "image/png", {
                        targetWidth: 1920,
                        targetHeight: 1080,
                        dpi: 72,
                        exportArea: "design"
                      });
                      
                      // Create a local objectURL for browser download
                      const localObjectUrl = URL.createObjectURL(exportBlob);
                      console.log("Created local object URL:", localObjectUrl);
                      
                      // Upload to Supabase
                      const exportFileName = `${templateId}-export-${Date.now()}.png`;
                      
                      try {
                        const { data, error: exportError } = await supabase.storage
                          .from('playmakerpics-images')
                          .upload(`exports/${userData.id}/${exportFileName}`, exportBlob, {
                            upsert: true,
                            cacheControl: 'no-cache',
                            contentType: 'image/png'
                          });
                        
                        if (exportError) throw exportError;
                        
                        const supabaseUrl = `${supabase.storage
                          .from('playmakerpics-images')
                          .getPublicUrl(`exports/${userData.id}/${exportFileName}`).data.publicUrl}?t=${Date.now()}`;
                        
                        console.log("✅ Custom export successful:", supabaseUrl);
                        
                        // Return both URLs
                        return { supabaseUrl, localObjectUrl, fileName: exportFileName };
                      } catch (uploadError) {
                        console.warn("Upload to Supabase failed, using local object URL:", uploadError);
                        // If supabase upload fails, still return the local URL
                        return { supabaseUrl: null, localObjectUrl, fileName: exportFileName };
                      }
                    } catch (exportError) {
                      console.error("Failed to export scene to Blob:", exportError);
                      
                      // Create a simple canvas fallback
                      try {
                        console.log("Attempting canvas fallback...");
                        const canvas = document.createElement('canvas');
                        canvas.width = 1920;
                        canvas.height = 1080;
                        const ctx = canvas.getContext('2d');
                        
                        // Fill with template color
                        ctx.fillStyle = '#4299E1';
                        ctx.fillRect(0, 0, canvas.width, canvas.height);
                        
                        // Add template name
                        ctx.fillStyle = 'white';
                        ctx.font = '72px sans-serif';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(templateId, canvas.width/2, canvas.height/2);
                        
                        // Convert to blob and create URL
                        const canvasPromise = new Promise(resolve => {
                          canvas.toBlob(async blob => {
                            const localObjectUrl = URL.createObjectURL(blob);
                            
                            // Also upload this fallback to the correct user folder in Supabase
                            try {
                              const fallbackFileName = `fallback-${templateId}-${Date.now()}.png`;
                              const { data, error: uploadError } = await supabase.storage
                                .from('playmakerpics-images')
                                .upload(`exports/${userData.id}/${fallbackFileName}`, blob, {
                                  upsert: true,
                                  cacheControl: 'no-cache',
                                  contentType: 'image/png'
                                });
                              
                              if (!uploadError) {
                                const fallbackUrl = supabase.storage
                                  .from('playmakerpics-images')
                                  .getPublicUrl(`exports/${userData.id}/${fallbackFileName}`).data.publicUrl;
                                
                                resolve({ 
                                  supabaseUrl: fallbackUrl, 
                                  localObjectUrl,
                                  fileName: fallbackFileName,
                                  fallback: true
                                });
                                return;
                              }
                            } catch (uploadErr) {
                              console.warn('Failed to upload fallback image to Supabase:', uploadErr);
                            }
                            
                            // If the upload fails, still return the local URL
                            resolve({ 
                              supabaseUrl: null, 
                              localObjectUrl,
                              fileName: `fallback-${templateId}-${Date.now()}.png`,
                              fallback: true
                            });
                          }, 'image/png');
                        });
                        
                        return await canvasPromise;
                      } catch (canvasError) {
                        console.error("Canvas fallback failed:", canvasError);
                        throw new Error("All export methods failed");
                      }
                    }
                  } catch (error) {
                    console.error("❌ Custom export failed:", error);
                    return null;
                  }
                };
                
                // Use our custom export function which already works
                const exportResult = await customExportTemplate(cesdkRef.current, template.id, replacementData);
                
                if (exportResult) {
                  exportUrl = exportResult.supabaseUrl || null;
                  localObjectUrl = exportResult.localObjectUrl || null;
                  console.log("Successfully exported template with CESDK:", exportUrl);
                  
                  // Add to the result with both URLs and ensure supabaseUrl takes priority
                  results.push({
                    id: template.id,
                    name: template.name,
                    url: exportUrl || localObjectUrl, // Use supabaseUrl if available, otherwise localObjectUrl
                    localUrl: localObjectUrl,
                    fileName: exportResult.fileName,
                    supabaseUrl: exportUrl, // Store the Supabase URL separately to ensure we use it for database
                    fromImage: imgIndex, // Track which image was used
                    imageUrl: imageUrl // Store reference to original image
                  });
                } else {
                  console.error(`Failed to export template ${template.name}`);
                  
                  // Still add to results with fallback to thumbnail
                  exportUrl = template.thumbnail_url || `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'><rect width='1920' height='1080' fill='%234299E1'/><text x='50%' y='50%' font-size='72' text-anchor='middle' fill='white'>${template.name} (CESDK Error)</text></svg>`;
                  
                  results.push({
                    id: template.id,
                    name: template.name,
                    url: exportUrl,
                    fromImage: imgIndex, // Track which image was used
                    imageUrl: imageUrl // Store reference to original image
                  });
                }
              } catch (cesdkError) {
                console.error("Error using CESDK for export:", cesdkError);
                // Fall back to thumbnail if CESDK export fails
                exportUrl = template.thumbnail_url || `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'><rect width='1920' height='1080' fill='%234299E1'/><text x='50%' y='50%' font-size='72' text-anchor='middle' fill='white'>${template.name}</text></svg>`;
              }
            } 
            // Fallback Approach: When CESDK is not available
            else {
              console.log(`No CESDK available, using fallback for template ${template.name}`);
              
              // Use the template's thumbnail or create a SVG placeholder
              exportUrl = template.thumbnail_url || `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'><rect width='1920' height='1080' fill='%234299E1'/><text x='50%' y='50%' font-size='72' text-anchor='middle' fill='white'>${template.name}</text></svg>`;
              
              // Add to results
              results.push({
                id: template.id,
                name: template.name,
                url: exportUrl,
                fromImage: imgIndex, // Track which image was used
                imageUrl: imageUrl // Store reference to original image
              });
            }
            
            // Try to insert into exports table with minimal fields
            // Only save if we have valid URLs
            if (exportUrl || localObjectUrl) {
              try {
                const currentResult = results[results.length - 1];
                
                // Validate the export URL before saving to the database
                let finalExportUrl = currentResult.supabaseUrl || currentResult.url;
                
                // Check if we have a valid URL that contains .png
                if (finalExportUrl && !finalExportUrl.includes('.png')) {
                  console.warn("Export URL doesn't contain .png, this may cause issues:", finalExportUrl);
                  
                  // If it's a .ubq file, attempt to correct the path
                  if (finalExportUrl.includes('.ubq')) {
                    // Try to fix the URL by changing .ubq to .png and ensuring it's in the exports/userId folder
                    const correctedUrl = finalExportUrl
                      .replace('.ubq', '.png')
                      .replace('/templates/', `/exports/${userData.id}/`)
                      .replace('/exports/', `/exports/${userData.id}/`); 
                    
                    console.log("Attempting to correct URL from .ubq to .png:", correctedUrl);
                    finalExportUrl = correctedUrl;
                  }
                }
                
                // Create the export data with only fields that exist in the database table
                const exportData = {
                  user_id: userData.id,
                  template_id: template.id,
                  export_url: finalExportUrl,
                  exported_at: new Date().toISOString(),
                  is_saved: false
                  // Don't include source_image_id and source_image_url as they don't exist in the exports table
                };

                // Store source image reference in client-side only
                const sourceImageData = {
                  id: currentImage.id,
                  url: imageUrl
                };
                
                console.log("Inserting into exports table with minimal fields:", exportData);
                
                const { data: insertedData, error: exportError } = await supabase
                  .from('exports')
                  .insert(exportData)
                  .select('id, export_url');
                
                if (exportError) {
                  console.error("Could not save to exports table. Error details:", {
                    message: exportError.message,
                    details: exportError.details,
                    hint: exportError.hint,
                    code: exportError.code
                  });
                } else {
                  console.log("✅ Saved to exports table successfully:", insertedData);
                  
                  // Add the export_url to results for future reference
                  if (insertedData && insertedData.length > 0) {
                    // Update the url in results to maintain consistent data
                    const resultIndex = results.findIndex(r => 
                      r.id === template.id && 
                      r.fromImage === imgIndex && 
                      r.imageUrl === imageUrl);
                      
                    if (resultIndex !== -1) {
                      results[resultIndex].databaseId = insertedData[0].id;
                      // Store source image info in the client-side results
                      results[resultIndex].sourceImage = sourceImageData;
                    }
                    
                    // Log association between export and source image (for debugging)
                    console.log(`Export ${insertedData[0].id} created from source image:`, sourceImageData);
                  }
                }
              } catch (dbError) {
                console.error("Error saving to exports table:", dbError);
              }
            }
          } catch (templateError) {
            console.error(`Error processing template ${template.name}:`, templateError);
            // Continue to next template
          }
        } // End of templates loop
      } // End of images loop
      
      if (results.length > 0) {
        setGeneratedTemplates(results);
        setProgress(`Successfully generated ${results.length} templates from ${approvedImages.length} images!`);
        setCompleted(true);
        setCustomizationStep(false);
        
        // Show success toast
        toast({
          title: "Templates generated!",
          description: `${results.length} templates have been created from ${approvedImages.length} approved images.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        
        // Auto close after successful completion
        setTimeout(() => {
          if (onClose) onClose();
        }, 3000);
      } else {
        throw new Error('No templates were successfully generated');
      }
    } catch (err) {
      console.error('Error in template generation:', err);
      setError("Failed to generate templates: " + err.message);
      setDebugInfo(JSON.stringify({
        error: err.message,
        stack: err.stack,
        name: err.name
      }, null, 2));
      toast({
        title: "Generation failed",
        description: err.message || 'Failed to generate templates',
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to fetch primary image
  const fetchPrimaryImage = async (imageId) => {
    try {
      const { data, error } = await supabase
        .from('images')
        .select('*')
        .eq('id', imageId)
        .single();
        
      if (error) throw error;
      if (!data) throw new Error("No image found with ID: " + imageId);
      
      console.log("Found primary image:", data);
      return data;
    } catch (err) {
      console.error("Failed to fetch primary image:", err);
      throw err;
    }
  };

  // Helper function to fetch approval data
  const fetchApprovalData = async (id) => {
    try {
      console.log("Fetching approval data for ID: " + id);
      
      const { data, error } = await supabase
        .from('image_approvals')
        .select('*')
        .eq('id', id)
        .single();
        
      if (error) throw error;
      
      if (!data) {
        throw new Error("No approval data found for ID: " + id);
      }
      
      console.log("Approval data:", data);
      return data;
    } catch (err) {
      console.error("Error fetching approval data:", err);
      throw err;
    }
  };

  // Helper function to fetch user data
  const fetchUserData = async (userId, eventId) => {
    try {
      console.log("Fetching user data for ID:", userId, "Event ID:", eventId);
      
      // Create a minimal user object that we'll use regardless of DB queries
      const baseUserData = {
        id: userId,
        name: "User " + userId.substring(0, 6),
        first_name: "",
        last_name: "",
        email: "",
      };
      
      // Skip app_users query entirely due to infinite recursion policy error
      console.log("Skipping app_users query due to policy issues");
      
      // Get event details - this should work
      try {
        const { data: eventData, error: eventError } = await supabase
          .from('events')
          .select('*')
          .eq('id', eventId)
          .limit(1);
          
        if (eventError) {
          console.error("Error fetching event data:", eventError);
          // Continue with minimal event data
        } else if (eventData && eventData.length > 0) {
          console.log("Event data retrieved:", eventData[0]);
          // Combine the data with event
          baseUserData.event = eventData[0];
        } else {
          console.warn("No event found with ID:", eventId);
          // Use minimal event data
          baseUserData.event = { id: eventId };
        }
      } catch (eventFetchError) {
        console.error("Failed to fetch event:", eventFetchError);
        // Use minimal event data
        baseUserData.event = { id: eventId };
      }
      
      // Try to get user details from a safer function if available
      try {
        const { data: userDetails, error: userDetailsError } = await supabase.rpc(
          'get_minimal_user_data',
          { user_id_param: userId }
        );
        
        if (!userDetailsError && userDetails) {
          console.log("Got user details from RPC:", userDetails);
          // Merge user details with base data
          Object.assign(baseUserData, userDetails);
        }
      } catch (userDetailsError) {
        console.warn("Could not fetch user details from RPC:", userDetailsError);
      }
      
      console.log("Using user data:", baseUserData);
      return baseUserData;
    } catch (err) {
      console.error("Failed to fetch user data:", err);
      
      // Last resort: return minimal data to continue the process
      const emergencyData = {
        id: userId,
        name: "User " + userId.substring(0, 6),
        first_name: "",
        last_name: "",
        email: "",
        event: { id: eventId }
      };
      
      console.log("Using emergency fallback data:", emergencyData);
      return emergencyData;
    }
  };

  // Helper function to fetch templates
  const fetchTemplates = async (eventId) => {
    try {
      console.log("Fetching event data for ID:", eventId);
      
      // First get the event to retrieve the templates list
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .limit(1); // Instead of single()
        
      if (eventError) {
        console.error("Error fetching event:", eventError);
        throw eventError;
      }
      
      if (!eventData || eventData.length === 0) {
        console.warn("No event found with ID:", eventId);
        throw new Error("Event not found");
      }
      
      const event = eventData[0];
      console.log("Event data retrieved:", event);
      
      // Parse the templates from the event data
      let templateIds = [];
      try {
        if (event.templates) {
          // Check if it's already an array
          if (Array.isArray(event.templates)) {
            templateIds = event.templates;
          } 
          // Check if it's a JSON string
          else if (typeof event.templates === 'string') {
            try {
              templateIds = JSON.parse(event.templates);
            } catch (jsonError) {
              // Try comma-separated format
              templateIds = event.templates.split(',').map(id => id.trim());
            }
          }
        }
      } catch (parseError) {
        console.error("Error parsing templates from event:", parseError);
        // If we can't parse it as JSON, try to use it as a comma-separated string
        if (typeof event.templates === 'string') {
          templateIds = event.templates.split(',').map(id => id.trim());
        }
      }
      
      if (!templateIds || templateIds.length === 0) {
        console.warn("No template IDs found for event:", eventId);
        throw new Error("No templates associated with this event");
      }
      
      console.log("Template IDs for event:", templateIds);
      
      // Now fetch each template by ID
      const validTemplates = [];
      for (const templateId of templateIds) {
        console.log("Fetching template with ID:", templateId);
        
        const { data: template, error: templateError } = await supabase
          .from('templates')
          .select('*')
          .eq('id', templateId)
          .limit(1); // Instead of single()
          
        if (templateError) {
          console.error("Error fetching template:", templateError);
          continue; // Skip this template but continue with others
        }
        
        if (template && template.length > 0) {
          validTemplates.push(template[0]);
        } else {
          console.warn("Template not found:", templateId);
        }
      }
      
      if (validTemplates.length === 0) {
        console.warn("No valid templates found for event");
        throw new Error("No valid templates found for this event");
      }
      
      console.log("Found " + validTemplates.length + " valid templates");
      return validTemplates;
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      throw err;
    }
  };

  // Helper function to find primary image for a user
  const findPrimaryImageForUser = async (userId) => {
    try {
      console.log("Finding primary image for user:", userId);
      
      // First try to find an upload marked as primary
      const { data: primaryUploads, error: primaryError } = await supabase
        .from('user_uploads')
        .select('*')
        .eq('user_id', userId)
        .eq('is_primary', true)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);
        
      if (!primaryError && primaryUploads?.length > 0) {
        console.log("Found primary image from user_uploads:", primaryUploads[0]);
        // Ensure we return with image_url for consistent access
        return {
          id: primaryUploads[0].id,
          image_url: primaryUploads[0].image_url || primaryUploads[0].file_url,
          file_url: primaryUploads[0].file_url,
          user_id: userId
        };
      }
      
      // If no primary image, try to get the most recent upload
      const { data: uploads, error: uploadsError } = await supabase
        .from('user_uploads')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (uploadsError) {
        console.error("Error fetching user uploads:", uploadsError);
        
        // Try a third approach - check storage for background-removed images
        console.log("Falling back to storage for user:", userId);
        const { data: images, error: storageError } = await supabase.storage
          .from('playmakerpics-images')
          .list(`background_removed_images/${userId}`, {
            limit: 1,
            sortBy: { column: 'created_at', order: 'desc' },
          });
          
        if (storageError) {
          console.error("Error checking storage:", storageError);
          throw new Error("Could not find any images for this user");
        }
        
        if (images?.length > 0) {
          const imageUrl = supabase.storage
            .from('playmakerpics-images')
            .getPublicUrl(`background_removed_images/${userId}/${images[0].name}`)
            .data.publicUrl;
            
          console.log("Found image in storage:", imageUrl);
          
          return {
            id: images[0].id || "storage-" + Date.now(),
            image_url: imageUrl,
            file_url: imageUrl,
            user_id: userId
          };
        }
        
        throw new Error("No images found for this user");
      }
      
      if (uploads?.length > 0) {
        console.log("Found most recent upload:", uploads[0]);
        // Ensure we return with image_url for consistent access
        return {
          id: uploads[0].id,
          image_url: uploads[0].image_url || uploads[0].file_url,
          file_url: uploads[0].file_url,
          user_id: userId
        };
      }
      
      throw new Error("No uploads found for this user");
    } catch (err) {
      console.error("Error finding primary image:", err);
      throw err;
    }
  };

  // Helper function to fetch all approved images for a user
  const fetchApprovedImages = async (userId, approvalId) => {
    try {
      console.log(`Fetching approved images for user: ${userId}, approval: ${approvalId}`);
      
      // Get images that were approved during the approval process
      const { data: approvedUploads, error: uploadsError } = await supabase
        .from('user_uploads')
        .select('*')
        .eq('user_id', userId)
        .eq('approval_id', approvalId)
        .eq('status', 'active')
        .eq('approval_status', 'approved')
        .order('created_at', { ascending: false });
      
      if (uploadsError) {
        console.error("Error fetching approved uploads:", uploadsError);
        throw uploadsError;
      }
      
      if (!approvedUploads || approvedUploads.length === 0) {
        console.warn("No approved uploads found for user:", userId);
        
        // Fallback: try to find any approved images for this user regardless of approval_id
        const { data: anyApprovedUploads, error: anyError } = await supabase
          .from('user_uploads')
          .select('*')
          .eq('user_id', userId)
          .eq('status', 'active')
          .eq('approval_status', 'approved')
          .order('created_at', { ascending: false });
        
        if (anyError) {
          console.error("Error in fallback query:", anyError);
          throw anyError;
        }
        
        if (!anyApprovedUploads || anyApprovedUploads.length === 0) {
          // Last resort: check if there's a primary image set
          console.log("Falling back to primary image check");
          const primaryImage = await findPrimaryImageForUser(userId);
          
          if (primaryImage) {
            console.log("Returning just the primary image as fallback");
            return [primaryImage];
          }
          
          throw new Error("No approved images found for this user");
        }
        
        console.log(`Found ${anyApprovedUploads.length} approved uploads through fallback query`);
        return anyApprovedUploads;
      }
      
      console.log(`Found ${approvedUploads.length} approved uploads for approval ${approvalId}`);
      
      // Format the data consistently
      const formattedImages = approvedUploads.map(upload => ({
        id: upload.id,
        image_url: upload.image_url || upload.file_url,
        file_url: upload.file_url,
        user_id: userId,
        is_primary: upload.is_primary
      }));
      
      return formattedImages;
    } catch (err) {
      console.error("Error fetching approved images:", err);
      throw err;
    }
  };

  return (
    <Box width="100%" p={6}>
      <VStack spacing={4} align="stretch">
        <Heading size="md">Automatic Template Generator</Heading>
        
        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertTitle>Error!</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {loading && (
          <VStack spacing={4}>
            <HStack>
              <Spinner size="sm" />
              <Text>{progress}</Text>
            </HStack>
            <Progress hasStripe isAnimated value={progress.includes('%') ? parseInt(progress) : undefined} 
                     isIndeterminate={!progress.includes('%')} width="100%" />
          </VStack>
        )}

        {/* Customization Step */}
        {!loading && customizationStep && primaryImage && (
          <VStack spacing={4} align="stretch">
            <Heading size="sm">Customize Your Templates</Heading>
            
            {/* Primary Image Preview */}
            <Box borderWidth="1px" borderRadius="md" p={3} mb={2} bg="white">
              <Text fontWeight="bold" mb={2}>Primary Image</Text>
              <Image 
                src={primaryImage} 
                alt="Primary image"
                maxH="150px"
                maxW="200px"
                objectFit="contain"
                mb={2}
                bg="white"
              />
              <Text fontSize="sm">This image will be used as the main subject in your generated templates.</Text>
            </Box>
            
            {/* CESDK Status Indicator */}
            <Alert status={cesdkAvailable ? "success" : "warning"} borderRadius="md" size="sm">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">{cesdkAvailable ? "Design Engine Ready" : "Limited Mode"}</AlertTitle>
                <AlertDescription fontSize="xs">
                  {cesdkAvailable 
                    ? "Templates will be customized with the Creative Editor SDK" 
                    : "Templates will use thumbnail previews only. Refresh the page if templates don't appear."}
                </AlertDescription>
              </Box>
            </Alert>
            
            <Divider my={2} />
            
            {/* Customization Form */}
            <form onSubmit={handleCustomizationSubmit}>
              <VStack spacing={4} align="stretch">
                {allPlaceholders.length > 0 ? (
                  <>
                    <Text>Customize the text for your templates:</Text>
                    {allPlaceholders.map((placeholder, index) => (
                      <FormControl key={index}>
                        <FormLabel>{placeholder.name}</FormLabel>
                        <Input
                          name={placeholder.name}
                          value={formData[placeholder.name] || ''}
                          onChange={handleInputChange}
                          placeholder={`Enter ${placeholder.name}`}
                        />
                      </FormControl>
                    ))}
                  </>
                ) : (
                  <Text>No customizable fields found for these templates.</Text>
                )}
                
                <Button 
                  type="submit" 
                  colorScheme="blue"
                  isLoading={loading}
                  mt={4}
                >
                  Generate Templates
                </Button>
              </VStack>
            </form>
          </VStack>
        )}
        
        {completed && generatedTemplates && generatedTemplates.length > 0 && (
          <VStack spacing={4}>
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              <AlertTitle>Success!</AlertTitle>
              <AlertDescription>
                {generatedTemplates.length} templates have been created from {approvedImages.length} approved images.
              </AlertDescription>
            </Alert>
            
            <Box>
              <Text fontWeight="bold" mb={2}>Generated Templates:</Text>
              {/* Group templates by source image for easier navigation */}
              {approvedImages.map((image, imageIndex) => {
                // Filter templates for this image
                const templatesForThisImage = generatedTemplates.filter(
                  template => template.fromImage === imageIndex || template.imageUrl === (image.image_url || image.file_url)
                );
                
                if (templatesForThisImage.length === 0) return null;
                
                return (
                  <Box key={imageIndex} mb={6}>
                    <Heading size="sm" mb={2}>
                      Image {imageIndex + 1} 
                      {image.is_primary && " (Primary Image)"}
                    </Heading>
                    {/* Small preview of source image */}
                    <Box mb={3} maxW="150px">
                      <Image 
                        src={image.image_url || image.file_url} 
                        alt={`Source image ${imageIndex + 1}`}
                        height="100px"
                        objectFit="contain"
                        bg="gray.100"
                        borderRadius="md"
                      />
                    </Box>
                    <SimpleGrid columns={[1, 2, 3]} spacing={4}>
                      {templatesForThisImage.map((template, idx) => (
                        <Box key={idx} borderRadius="md" overflow="hidden" borderWidth="1px" bg="white">
                          <Image 
                            src={template.url} 
                            alt={`Generated template ${template.name}`} 
                            width="100%" 
                            bg="white"
                            fallback={<Spinner />} 
                            onError={(e) => {
                              console.error(`Failed to load image: ${template.url}`);
                              e.target.onerror = null;
                              e.target.src = `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><rect width='200' height='200' fill='%23FF8C42'/><text x='50%' y='50%' font-size='24' text-anchor='middle' fill='white'>${template.name}</text></svg>`;
                            }}
                          />
                          <Box p={2}>
                            <Text fontSize="sm">{template.name || "Template " + (idx + 1)}</Text>
                            {template.localUrl && (
                              <Link 
                                href={template.localUrl} 
                                download={template.fileName || "generated-template.png"}
                                display="flex"
                                alignItems="center"
                                fontSize="sm"
                                color="blue.600"
                                mt={1}
                              >
                                <FaDownload style={{ marginRight: '5px' }} />
                                Download
                              </Link>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Box>
                );
              })}
            </Box>
            
            <Button onClick={onClose} colorScheme="blue">Close</Button>
          </VStack>
        )}
        
        {completed && (!generatedTemplates || generatedTemplates.length === 0) && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <AlertTitle>No templates generated</AlertTitle>
            <AlertDescription>
              The process completed but no templates were generated. Please try again.
            </AlertDescription>
          </Alert>
        )}
        
        {debugInfo && (
          <Box mt={4} p={3} bg="gray.100" borderRadius="md">
            <Heading size="sm" mb={2}>Debug Information:</Heading>
            <Code display="block" whiteSpace="pre" overflowX="auto" p={2}>
              {debugInfo}
            </Code>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default AutoTemplateGenerator;